import { createSlice } from "@reduxjs/toolkit";

import {
  getFloorThunk,
  createFloorThunk,
  updateFloorThunk,
  deleteFloorThunk,
} from "state/action/floor";

// Helper get building
const getBuildingIdFromAction = (action) => {
  try {
    const qp = (action?.meta?.arg?.queryParams || "").replace(/^\?/, "");
    const params = new URLSearchParams(qp);
    return params.get("building") || "";
  } catch {
    return "";
  }
};

// Initial state
const initialState = {
  // Get Floor inisial state
  byBuilding: {},
  getFloor: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  // Create Floor inisial state
  createFloor: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  // Update Floor inisial state
  updateFloor: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  // Delete Floor inisial state
  deleteFloor: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  createFloorInputBody: {
    property: "",
    building: "",
    name: "",
    code: "",
    floorPlanUrl: "",
    area: {
      total_sqm: "",
      rentable_sqm: "",
    },
    description: "",
    status: "",
  },
  selectedFloor: {
    property: "",
    building: "",
    name: "",
    code: "",
    floorPlanUrl: "",
    base_price: "",
    area: {
      total_sqm: "",
      rentable_sqm: "",
    },
    description: "",
    status: "",
  },
  // Floor object value
  floor_object_value: "",
};

export const floorSlice = createSlice({
  name: "floor",
  initialState,
  reducers: {
    setFloorField: (state, action) => {
      const { field, value } = action.payload;
      state.createFloorInputBody[field] = value;
    },
    // Reducer khusus untuk nested object 'area'
    setAreaField: (state, action) => {
      const { field, value } = action.payload; // field: 'total_sqm' or 'rentable_sqm'
      state.createFloorInputBody.area[field] = value;
    },
    resetFloorForm: (state) => {
      state.createFloorInputBody = initialState.createFloorInputBody;
    },
    setGetFloorObjectValue: (state, action) => {
      return {
        ...state,
        floor_object_value: action.payload,
      };
    },
    setCreateFloorInputBody: (state, action) => {
      return {
        ...state,
        createFloorInputBody: action.payload,
      };
    },
    setSelectedFloor: (state, action) => {
      return {
        ...state,
        selectedFloor: action.payload,
      };
    },
    clearFloorsByBuilding: (state, action) => {
      const buildingId = action.payload;
      const next = { ...(state.byBuilding || {}) };
      if (buildingId in next) delete next[buildingId];
      return {
        ...state,
        byBuilding: next,
      };
    },
    // note: clear state
    setClearGetFloorObjectValue: (state, _) => {
      return {
        ...state,
        floor_object_value: "",
      };
    },
    setClearCreateFloorInputBody: (state, _) => {
      return {
        ...state,
        createFloorInputBody: {
          property: "",
          building: "",
          name: "",
          code: "",
          floorPlanUrl: "",
          area: {
            total_sqm: "",
            rentable_sqm: "",
          },
          description: "",
          status: "",
        },
      };
    },
    setClearGetFloor: (state, _) => {
      return {
        ...state,
        getFloor: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
      };
    },
    setClearCreateFloor: (state, _) => {
      return {
        ...state,
        createFloor: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
      };
    },
    setClearUpdateFloor: (state, _) => {
      return {
        ...state,
        updateFloor: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
      };
    },
    setClearDeleteFloor: (state, _) => {
      return {
        ...state,
        deleteFloor: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
      };
    },
    setClearAllFloorState: (state, _) => {
      return {
        ...state,
        byBuilding: {},
        // Get Floor inisial state
        getFloor: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
        // Create Floor inisial state
        createFloor: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
        // Update Floor inisial state
        updateFloor: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
        // Delete Floor inisial state
        deleteFloor: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
        // Floor object value
        Floor_object_value: "",
      };
    },
  },
  extraReducers(builder) {
    builder
      // Get Floor reducer
      .addCase(getFloorThunk.pending, (state, _) => {
        return {
          ...state,
          getFloor: {
            data: null,
            isPending: true,
            isFulfilled: false,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(getFloorThunk.fulfilled, (state, action) => {
        const buildingId = getBuildingIdFromAction(action);
        return {
          ...state,
          getFloor: {
            data: action.payload,
            isPending: false,
            isFulfilled: true,
            isRejected: false,
            err: null,
          },
          byBuilding: buildingId
            ? {
                ...(state.byBuilding || {}),
                [buildingId]: action.payload,
              }
            : { ...(state.byBuilding || {}) },
        };
      })
      .addCase(getFloorThunk.rejected, (state, action) => {
        return {
          ...state,
          getFloor: {
            data: null,
            isPending: false,
            isFulfilled: false,
            isRejected: true,
            err: action.error.message,
          },
        };
      })
      // Create Floor reducer
      .addCase(createFloorThunk.pending, (state, _) => {
        return {
          ...state,
          register: {
            data: null,
            isPending: true,
            isFulfilled: false,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(createFloorThunk.fulfilled, (state, action) => {
        return {
          ...state,
          register: {
            data: action.payload,
            isPending: false,
            isFulfilled: true,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(createFloorThunk.rejected, (state, action) => {
        return {
          ...state,
          register: {
            data: null,
            isPending: false,
            isFulfilled: false,
            isRejected: true,
            err: action.error.message,
          },
        };
      })
      // update Floor reducer
      .addCase(updateFloorThunk.pending, (state, _) => {
        return {
          ...state,
          updateFloor: {
            data: null,
            isPending: true,
            isFulfilled: false,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(updateFloorThunk.fulfilled, (state, action) => {
        return {
          ...state,
          updateFloor: {
            data: action.payload,
            isPending: false,
            isFulfilled: true,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(updateFloorThunk.rejected, (state, action) => {
        return {
          ...state,
          updateFloor: {
            data: null,
            isPending: false,
            isFulfilled: false,
            isRejected: true,
            err: action.error.message,
          },
        };
      })
      // delete Floor reducer
      .addCase(deleteFloorThunk.pending, (state, _) => {
        return {
          ...state,
          deleteFloor: {
            data: null,
            isPending: true,
            isFulfilled: false,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(deleteFloorThunk.fulfilled, (state, action) => {
        return {
          ...state,
          deleteFloor: {
            data: action.payload,
            isPending: false,
            isFulfilled: true,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(deleteFloorThunk.rejected, (state, action) => {
        return {
          ...state,
          deleteFloor: {
            data: null,
            isPending: false,
            isFulfilled: false,
            isRejected: true,
            err: action.error.message,
          },
        };
      });
  },
});

export const floorAction = {
  ...floorSlice.actions,
  getFloorThunk,
  createFloorThunk,
  updateFloorThunk,
  deleteFloorThunk,
};

export function selectFloorObjectValue(state) {
  return state?.floor?.floor_object_value;
}

export function selectFloor(state) {
  return state?.floor;
}

export const selectFloorsByBuilding = (buildingId) => (state) =>
  state?.floor?.byBuilding?.[buildingId]?.data?.data || [];

export default floorSlice.reducer;
